import { Card, Tag } from '../../ui'
import { getWorkspace } from '../../lib/api'
import { agrees, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import { describeFailure, useApi } from './useApi'
import { planPresentation } from '../plan'
import { SupportLink } from '../../lib/support'
import './live.css'

/** A plan as its name, not its stored value ("monitor"). Unknown values are shown as they are. */
export function planName(plan: string): string {
  return plan === 'monitor' ? 'Monitor' : plan === 'free' ? 'Free' : plan
}

/** A subscription state in words ("Past due", not "past_due"). */
function planStatusName(status: string): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'past_due':
      return 'Past due'
    case 'canceled':
      return 'Cancelled'
    default:
      return status
  }
}

/**
 * What this workspace is on, and what that means.
 *
 * The plan is read from the workspace's own row rather than inferred from behaviour. The banner
 * in the shell already says "free assessment"; if this page decided the same thing a second way,
 * the two could disagree on the day somebody's plan changes and the customer would be left
 * choosing which screen to believe.
 *
 * NO CHECKOUT, and no "Upgrade" that opens nothing. There is no billing integration in this
 * product — no price is charged, no card is stored, no endpoint exists to change a plan. A page
 * that renders a pricing table with a button is a page that will take somebody's intent to buy
 * and drop it, which is worse for a paid product than admitting the door is not open yet.
 */
export function LivePlans() {
  const session = useSession()
  const settings = useApi(() => getWorkspace(), [])

  if (settings.state === 'loading') {
    return <div className="live-empty">Loading…</div>
  }

  if (settings.state === 'error') {
    return <div className="live-error">{describeFailure(settings.status)}</div>
  }

  if (!agrees(session, settings.data.workspaceId)) {
    return (
      <div className="live-error">
        {session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}
      </div>
    )
  }

  const { plan, planStatus } = settings.data
  // The SAME presentation the shell uses, so this page and the shell chrome cannot disagree about
  // whether monitoring is on. THREE states, not two: monitoring ON (paid + active), genuinely FREE,
  // and a Monitor plan that is OFF right now (past due / canceled / none). A lapsed Monitor customer
  // must NOT be shown the free copy -- they are a paying customer whose plan is paused, not a free
  // user -- so the "free" wording keys on the plan value, never on monitoringActive alone.
  const { monitoringActive, banner } = planPresentation(plan, planStatus)
  const isFreePlan = plan === 'free'
  const reviewAvailable = monitoringActive && settings.data.recordedEvidenceReviewAvailable === true

  return (
    <>
      {/* YOUR PLAN FIRST, in one band, with how to change it beside it (owner approved, 2026-10-11). */}
      <section className="plan-band" aria-labelledby="plan-band-title">
        <div className="plan-band-main">
          <h2 id="plan-band-title" className="plan-band-label">Your plan</h2>
          <div className="plan-current">
            <span className="plan-band-name">{planName(plan)}</span>

            {/* 'none' is the plan_status of a workspace that has never had a paid plan, which is
                not a problem and must not be shown as one. Anything else is surfaced -- if a
                subscription is past due, the customer needs to be told -- in words rather than the
                stored value, and in the warning tone ONLY when it is a problem: an amber "active"
                read as an alert about a subscription that was fine. */}
            {planStatus !== 'none' ? (
              <Tag tone={planStatus === 'active' ? 'outline' : 'warn'}>{planStatusName(planStatus)}</Tag>
            ) : null}
          </div>

          <p className="settings-lede" style={{ marginTop: 'var(--space-2)' }}>
            {monitoringActive
              ? 'This workspace is on the Monitor plan. Your tenant is re-assessed on a schedule and ' +
                'you are alerted to new findings and changes, on top of the assessments you run ' +
                'yourself. The report stays readable.'
              : isFreePlan
                ? 'A free assessment runs once and the report stays readable. Nothing re-checks your ' +
                  'tenant on a schedule, so what you are reading is true as of the last assessment ' +
                  'and not as of today.'
                : /* a Monitor plan that is not active right now: past due / canceled / none. Say so
                     honestly -- the same wording the shell banner uses -- never "free". */
                  (banner ?? 'Continuous monitoring is not active on this workspace right now.')}
          </p>
        </div>

        <div className="plan-band-side">
          <p className="plan-band-side-title">Changing plan</p>
          <p className="settings-note" style={{ marginTop: 0 }}>
            Not self-service yet: email{' '}
            <SupportLink subject={`Change the plan of ${settings.data.name}`} /> and we will move the
            workspace over once terms are agreed.
          </p>
        </div>
      </section>

      {/*
        THE TWO PLANS IN ONE TABLE, with no prices (a decision: 2026-09-24, "show no numbers yet").
        Each feature is named once with its explanation under it and a mark in each plan's column, so
        the difference between the plans is the column of dashes rather than two lists to read
        against each other (owner approved, 2026-10-11). Every row is something the product does
        today -- a comparison, not a roadmap -- and the Monitor rows are exactly what
        auth.is_on_monitor gates: Azure, the AI area, your own AI, scheduled re-assessment, change
        alerts and the weekly summary. Everything else is on both plans.
      */}
      {/* Not in a sideways-scrolling region: three columns fit a phone, and the plan names stay
          in view as the page scrolls, which a scrolling box would stop. */}
      <div className="plan-table-wrap">
        <table className="plan-table">
          <thead>
            <tr>
              <th scope="col" className="plan-table-feature"><span className="sr-only">Feature</span></th>
              <th scope="col" className={isFreePlan ? 'plan-col is-current' : 'plan-col'}>
                <span className="plan-col-name">Free</span>
                <span className="plan-col-tagline">A full assessment whenever you ask for one.</span>
                {/* Not "Free" again under the name "Free" -- it read as a duplicated heading. True as
                    written: there is no checkout, and no card is ever taken. */}
                <span className="plan-col-price">No charge — no card needed</span>
                {isFreePlan ? <Tag tone="accent">Your plan</Tag> : null}
              </th>
              <th scope="col" className={!isFreePlan ? 'plan-col is-current' : 'plan-col'}>
                <span className="plan-col-name">Monitor</span>
                <span className="plan-col-tagline">Oversight between assessments.</span>
                <span className="plan-col-price">Priced by organisation size — contact us</span>
                {!isFreePlan ? (
                  <Tag tone={monitoringActive ? 'accent' : 'warn'}>
                    {monitoringActive ? 'Your plan' : 'Your plan — not active'}
                  </Tag>
                ) : null}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="plan-group">
              <th colSpan={3} scope="colgroup">On both plans</th>
            </tr>
            {BOTH.map((f) => <FeatureRow key={f.name} feature={f} free isFreePlan={isFreePlan} />)}
            <tr className="plan-group">
              <th colSpan={3} scope="colgroup">Monitor adds</th>
            </tr>
            {MONITOR_ONLY.map((f) => <FeatureRow key={f.name}
              feature={f.name === 'Your own AI' && reviewAvailable
                ? { ...f, what: f.what + ' Recorded-evidence review is a separate owner-only pilot, available only in approved workspaces.' }
                : f}
              free={false} isFreePlan={isFreePlan} />)}
          </tbody>
        </table>
      </div>

      <p className="settings-note plan-table-foot">
        Each person chooses which of these emails they receive, in{' '}
        <a href="/settings/account">Settings, under Your account</a>.
      </p>

      <p className="settings-lede" style={{ marginTop: 'var(--space-3)' }}>
        We do not publish prices yet: Monitor is priced by the size of your organisation. Email{' '}
        <SupportLink subject={`Monitor pricing for ${settings.data.name}`} /> for a quote. Managing
        several customers' tenants? Ask about pricing for managed service providers.
      </p>

      <div className="plan-notes">
        <Card title="What no plan does">
          <ul className="plan-list">
            {/* Only true when monitoring is off. A paying, active Monitor customer must never be told
                on their own plan page that continuous monitoring is off -- that was the bug. */}
            {!monitoringActive ? (
              <li>
                {/* "Paused" for a Monitor plan that has lapsed: it is theirs, and comes back. */}
                <strong>
                  {isFreePlan ? 'Continuous monitoring is off.' : 'Continuous monitoring is paused.'}
                </strong>{' '}
                Nothing re-assesses your tenant on a schedule, so a setting changed the day after an
                assessment is not noticed until you run another one.
              </li>
            ) : null}
            <li>
              {/* Not "Defenssive holds read-only permissions in your tenant": the Terms and the
                  privacy notice say its two Exchange permissions are not read-only by name. What it
                  does is the claim to make. */}
              <strong>Nothing is remediated for you.</strong> Defenssive only reads, on every plan:
              it makes no change in your tenant. Remediation steps are written for you to carry out.
            </li>
            <li>
              <strong>Does not claim SCuBA coverage it doesn't have.</strong> Defenssive's endpoint
              and data checks are not mapped to SCuBA policies — they are shown as additional
              checks, kept out of the CISA count. Microsoft Secure Score is Microsoft's own number,
              shown as it is and not counted either.
            </li>
          </ul>
        </Card>

        <Card title="Changing plan">
          {/* The honest version. There is no billing integration: no price is charged, no card is
              stored, and no endpoint exists to move a workspace between plans. An "Upgrade" button
              here would collect somebody's intent to buy and drop it on the floor. */}
          <p className="settings-lede">
            Changing plan is not self-service yet. There is no checkout in the product — no card is
            stored and no plan can be changed from this page. Email{' '}
            <SupportLink subject={`Change the plan of ${settings.data.name}`} /> and we will move the
            workspace over once terms are agreed.
          </p>

          <p className="settings-note" style={{ marginTop: 'var(--space-2)' }}>
            Your current plan is shown above exactly as it is recorded against this workspace, so
            it is the same value billing would act on.
          </p>
        </Card>
      </div>
    </>
  )
}

/** A feature, in the plan page's own words: its name, then what it means. */
type Feature = { name: string; what: string }

/** On both plans. */
const BOTH: readonly Feature[] = [
  {
    name: 'Assesses your tenant against the CISA SCuBA baselines',
    what: 'Control by control, with the evidence behind each finding. Today that is identity (Entra ID) and email (Exchange Online).',
  },
  {
    name: 'Adds security checks outside SCuBA',
    what: 'Endpoint findings from Microsoft Defender and Defenssive’s own checks, kept separate from your SCuBA coverage — and shows your Microsoft Secure Score as Microsoft reports it.',
  },
  { name: 'Says what it could not check', what: 'An area that could not run is shown as not assessed, never as clean.' },
  { name: 'Tracks the work', what: 'Owners, progress and set-aside decisions on each finding, with the reason kept on record.' },
  {
    name: 'Reports you can hand on',
    what: 'Print or PDF, a CSV of every finding, and a sealed record an auditor can re-verify. They do not expire or lock.',
  },
  { name: 'Your team', what: 'Invite admins, members and read-only auditors.' },
]

/** What Monitor adds: exactly what auth.is_on_monitor gates. */
const MONITOR_ONLY: readonly Feature[] = [
  {
    name: 'Azure',
    what: 'The configuration of the Azure subscriptions you give Defenssive the Reader role, assessed weekly and whenever you start an assessment.',
  },
  {
    name: 'The AI area',
    what: 'How your Azure OpenAI and AI Foundry resources, AI agents and AI applications are set up, and what each can reach.',
  },
  {
    name: 'Your own AI',
    what: 'Explain a finding or draft a fix plan with your organisation’s Azure OpenAI, if your workspace owner turns it on.',
  },
  { name: 'Re-assesses your tenant on a schedule', what: 'So a change is caught without anyone having to run an assessment.' },
  {
    name: 'Change alerts by email',
    what: 'After a scheduled or onboarding assessment records a new or returning finding, or cannot complete. They follow assessments, not live Microsoft changes.',
  },
  {
    name: 'A weekly summary email',
    what: 'What changed and what is still outstanding. A quiet, fully-assessed week may send nothing.',
  },
]

/**
 * One row of the comparison. The marks are drawn, and each is also said in words ("Included" or
 * "Not included") for a screen reader, under the plan's column heading.
 */
function FeatureRow({ feature, free, isFreePlan }: { feature: Feature; free: boolean; isFreePlan: boolean }) {
  return (
    <tr>
      <th scope="row">
        <span className="plan-feature-name">{feature.name}</span>
        <span className="plan-feature-what">{feature.what}</span>
      </th>
      <td className={isFreePlan ? 'plan-mark is-current' : 'plan-mark'}>
        <Mark included={free} />
      </td>
      <td className={!isFreePlan ? 'plan-mark is-current' : 'plan-mark'}>
        <Mark included />
      </td>
    </tr>
  )
}

function Mark({ included }: { included: boolean }) {
  return included ? (
    <>
      <span className="plan-yes" aria-hidden="true">
        <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2 6.5 5 9.5 10 3" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
      </span>
      <span className="sr-only">Included</span>
    </>
  ) : (
    <>
      <span className="plan-no" aria-hidden="true">—</span>
      <span className="sr-only">Not included</span>
    </>
  )
}
